import { forwardRef, useRef, useState, useEffect } from "react";
import DatePickerLib from "react-datepicker";
import {
  autoUpdate,
  flip,
  offset,
  shift,
} from "@floating-ui/react";
import { Calendar } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.css";

/**
 * Custom Floating UI config for react-datepicker when the calendar is portaled
 * to `document.body` (modals / scrolling detail pages). Keep the floating
 * strategy fixed, then pass the viewport middleware through `popperModifiers`
 * (react-datepicker v9 ignores middleware nested in `popperProps`).
 */
const PORTALED_DATEPICKER_POPPER_PROPS = {
  strategy: "fixed" as const,
  whileElementsMounted: autoUpdate,
};

const PORTALED_DATEPICKER_POPPER_MODIFIERS = [
  offset(10),
  flip({
    mainAxis: false,
    crossAxis: true,
    padding: 12,
    rootBoundary: "viewport",
  }),
  shift({ padding: 12, crossAxis: true }),
];

/** `mdy` = MM/DD/YYYY (default for Application). `dmy` = DD/MM/YYYY. */
export type EditDatePickerDisplayFormat = "dmy" | "mdy";

interface EditDatePickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  allowFutureDates?: boolean;
  allowPastDates?: boolean;
  required?: boolean;
  /** How the text field and calendar label dates (value stays YYYY-MM-DD). */
  displayFormat?: EditDatePickerDisplayFormat;
  /**
   * When set, react-datepicker renders the calendar in a `document.body` portal
   * and uses `position: fixed` placement. Use inside modals that apply
   * `overflow: hidden` or `transform` (otherwise the popper clips or jumps to the
   * wrong place — see Job End Date in Income & Employment).
   */
  calendarPortalId?: string;
}

// Format YYYY-MM-DD for the text input
const formatDisplayDate = (
  val: string,
  displayFormat: EditDatePickerDisplayFormat,
): string => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [year, month, day] = val.split("-");
    if (displayFormat === "mdy") {
      return `${month}/${day}/${year}`;
    }
    return `${day}/${month}/${year}`;
  }
  return val;
};

const formatTypedDate = (rawValue: string): string => {
  const digits = rawValue.replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";
  const first = digits.slice(0, 2);
  const second = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  const parts = [first, second, year].filter(Boolean);
  const formatted = parts.join("/");

  // Keep a trailing slash while the user has just completed month/day.
  if ((digits.length === 2 || digits.length === 4) && rawValue.endsWith("/")) {
    return `${formatted}/`;
  }
  return formatted;
};

const parseDisplayDate = (
  input: string,
  displayFormat: EditDatePickerDisplayFormat,
): Date | null => {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(input)) return null;
  const [first, second, yearValue] = input.split("/").map(Number);
  const month = displayFormat === "mdy" ? first : second;
  const day = displayFormat === "mdy" ? second : first;
  const year = yearValue;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

// Custom input component to ensure correct display format
interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  // Added specific props to avoid collision with react-datepicker's injected props
  inputValue?: string;
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const CustomDateInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      placeholder,
      disabled,
      inputValue,
      onInputChange,
    },
    ref,
  ) => (
    <input
      ref={ref}
      type="text"
      className="edit-date-picker-input"
      // Use inputValue if provided (controlled by us), otherwise fall back to value (controlled by datepicker)
      value={inputValue !== undefined ? inputValue : value || ""}
      onClick={onClick}
      // Use onInputChange if provided (controlled by us), otherwise fall back to onChange
      onChange={onInputChange || onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
    />
  ),
);
CustomDateInput.displayName = "CustomDateInput";

/**
 * EditDatePicker - A styled date picker for edit forms in Application Detail
 * Shows date as text with a calendar icon to pick the date
 */
export function EditDatePicker({
  value,
  onChange,
  onBlur,
  disabled = false,
  placeholder,
  className = "",
  minDate,
  maxDate,
  allowFutureDates = false,
  allowPastDates = true,
  required = false,
  calendarPortalId,
  displayFormat = "mdy",
}: EditDatePickerProps) {
  const placeholderDefault =
    displayFormat === "mdy" ? "MM/DD/YYYY" : "DD/MM/YYYY";
  const resolvedPlaceholder = placeholder ?? placeholderDefault;
  const pickerDateFormat =
    displayFormat === "mdy" ? "MM/dd/yyyy" : "dd/MM/yyyy";
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse the value to a Date object - handle YYYY-MM-DD format explicitly
  const parseValueToDate = (val: string): Date | null => {
    if (!val) return null;

    // Handle YYYY-MM-DD format explicitly to avoid browser interpretation issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [year, month, day] = val.split("-").map(Number);
      // Month is 0-indexed in JavaScript Date
      return new Date(year, month - 1, day);
    }

    // Fallback for other formats
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const selectedDate = parseValueToDate(value);

  // Set min/max dates based on props
  const minDateValue = allowPastDates ? minDate : new Date();
  const maxDateValue = allowFutureDates ? maxDate : maxDate || new Date();

  const [inputValue, setInputValue] = useState("");

  // Sync internal state with prop value changes
  useEffect(() => {
    setInputValue(formatDisplayDate(value, displayFormat));
  }, [value, displayFormat]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Format date as YYYY-MM-DD for storage
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;
      onChange(formattedDate);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = formatTypedDate(e.target.value);
    setInputValue(newVal);

    if (!newVal) {
      onChange("");
      return;
    }

    const date = parseDisplayDate(newVal, displayFormat);
    if (!date) return;
    if (minDateValue && date < minDateValue) return;
    if (maxDateValue && date > maxDateValue) return;
    handleDateChange(date);
  };

  const handleBlur = () => {
    // On blur, revert to the last valid formatted date from props if current input is invalid/incomplete
    const formattedCurrent = formatDisplayDate(value, displayFormat);
    if (inputValue !== formattedCurrent) {
      setInputValue(formattedCurrent);
    }
    if (onBlur) onBlur();
  };

  return (
    <div className={`edit-date-picker-wrapper ${className}`}>
      <DatePickerLib
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={pickerDateFormat}
        onBlur={handleBlur}
        disabled={disabled}
        placeholderText={resolvedPlaceholder}
        minDate={minDateValue}
        maxDate={maxDateValue}
        customInput={
          <CustomDateInput
            ref={inputRef}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
          />
        }
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        yearDropdownItemNumber={120}
        scrollableYearDropdown
        popperClassName={
          calendarPortalId
            ? "date-picker-popper date-picker-popper--portaled"
            : "date-picker-popper"
        }
        popperPlacement="bottom-start"
        {...(calendarPortalId
          ? {
              portalId: calendarPortalId,
              popperProps: PORTALED_DATEPICKER_POPPER_PROPS,
              popperModifiers: PORTALED_DATEPICKER_POPPER_MODIFIERS,
            }
          : {})}
        required={required}
      />
      <Calendar className="edit-date-picker-icon" />
    </div>
  );
}
